import styled from "styled-components"

import { Badge, spacing, Text } from "@minuthq/meatball-ui-react"
import { AxiosError } from "axios"

import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { getStockBadgeColor } from "src/components/Paradise/ParadiseWarehouses/paradiseWarehousesUtils"
import {
  ParadiseLayout,
  ParadiseTitle,
  TopWrapper,
} from "src/components/Paradise/sharedStyles"
import { useFetchWarehouses } from "src/data/ecommerce/ecommerceQueries"
import { TWarehouse } from "src/data/ecommerce/ecommerceTypes"
import { prettifyApiFieldValue } from "src/data/orders/orderUtils"
import { Routes } from "src/router/routes"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { Titlebar } from "src/ui/Layout/Titlebar"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { formatUtcDate } from "src/utils/l10n"

export function ParadiseWarehouses() {
  const fetchWarehouses = useFetchWarehouses({
    options: { keepPreviousData: true },
  })

  const tableColumns: TableColumn<TWarehouse, string>[] = [
    {
      label: "ID",
      value: "id",
      columnWidth: "min-content",
      renderCell: (data) => (
        <InternalLink to={Routes.ParadiseWarehouse.location(data.id)}>
          {data.id}
        </InternalLink>
      ),
      disabled: true,
    },
    {
      label: "Name",
      value: "name",
      columnWidth: "min-content",
      renderCell: (data) => <div>{prettifyApiFieldValue(data.name)}</div>,
    },
    {
      label: "WMS",
      value: "warehouse_management_system",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{prettifyApiFieldValue(data.warehouse_management_system)}</div>
      ),
    },
    {
      label: "Legal Entity",
      value: "legal_entity",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{prettifyApiFieldValue(data.legal_entity)}</div>
      ),
    },
    {
      label: "SKU stocks",
      value: "sku_stocks",
      columnWidth: "min-content",
      renderCell: (data) => {
        const limit = 8

        return (
          <div>
            <SKUsWrapper>
              {data.articles.slice(0, limit).map((article) => (
                <Badge
                  key={article.sku}
                  size="x-small"
                  color={getStockBadgeColor({
                    stockQuantity: article.stock_quantity,
                    lowStockThreshold: article.low_stock_threshold,
                    outOfStockThreshold: article.out_of_stock_threshold,
                  })}
                >
                  {article.sku}: {article.stock_quantity}
                </Badge>
              ))}
              {data.articles.length > limit && (
                <MTooltip
                  title={data.articles.slice(limit).map((article) => (
                    <Badge
                      key={article.sku}
                      size="x-small"
                      color={getStockBadgeColor({
                        stockQuantity: article.stock_quantity,
                        lowStockThreshold: article.low_stock_threshold,
                        outOfStockThreshold: article.out_of_stock_threshold,
                      })}
                    >
                      {article.sku}: {article.stock_quantity}
                    </Badge>
                  ))}
                >
                  <Text
                    variant="bodyS"
                    color="secondary"
                    textDecoration="underline"
                    style={{
                      textDecorationStyle: "dashed",
                    }}
                  >
                    more
                  </Text>
                </MTooltip>
              )}
            </SKUsWrapper>
          </div>
        )
      },
    },
    {
      label: "Stock Updated At",
      value: "stock_quantity_updated_at",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{formatUtcDate({ date: data.stock_quantity_updated_at })}</div>
      ),
    },
    {
      label: "Created At",
      value: "created_at",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{formatUtcDate({ date: data.created_at })}</div>
      ),
    },
    {
      label: "Updated At",
      value: "updated_at",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{formatUtcDate({ date: data.updated_at })}</div>
      ),
    },
  ]

  const {
    headerElements,
    interactiveColumns,
    interactiveVisibleColumns,
    rows,
    updateColumnVisibility,
    templateColumns,
  } = useTableColumns({
    columns: tableColumns,
    data: fetchWarehouses.data,
    options: {
      localStorageKey: "minut.paradise.warehouses",
    },
  })

  const errorMessage =
    (fetchWarehouses.error as AxiosError | undefined)?.message || ""

  return (
    <ParadiseLayout>
      <Titlebar
        bottomMargin={spacing.S}
        title={
          <ParadiseTitle>
            Warehouses
            <Badge color="info">
              Total: {fetchWarehouses.data?.length ?? 0}
            </Badge>
          </ParadiseTitle>
        }
      />

      <TopWrapper>
        <div />
        <DropdownMultiSelect
          label="Columns"
          selectedValues={interactiveVisibleColumns.map((c) => c.value)}
          options={interactiveColumns}
          onChange={({ checked, option }) => {
            updateColumnVisibility(option.value, !checked)
          }}
        />
      </TopWrapper>

      <ParadiseTable
        header={headerElements}
        rows={rows ?? []}
        templateColumns={templateColumns}
        error={{
          hasError: fetchWarehouses.isError,
          title: errorMessage,
        }}
        isLoadingMore={fetchWarehouses.isPreviousData}
        isLoading={fetchWarehouses.isLoading}
      />
    </ParadiseLayout>
  )
}

const SKUsWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing.XS};
`
